import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { ClientSettingName, IClientSettings } from "@vmosolutions/oc-interfaces";
import { getClientSetting } from "../actions/App.Actions";

export enum DelayCodeSortOption {
  DelayCodeTitle = "DelayCodeTitle",
  DelayCodeNumber = "DelayCodeNumber",
  DelayTitle = "DelayTitle",
}
export interface IUISettingsState {
  flightReleaseAutoSelectContent?: boolean;
  delayCodeFormat?: string[];
  delayCodeSortOrder?: DelayCodeSortOption;
  flightTimelineCopyTextFormat?: (string | string[])[];
  flightCancellationReasons?: string[];
  delayCodeAllowControllable?: boolean;
  flightTimesEqual?: boolean;
  crewScheduleFetchIntervalDays?: number;
  enableVFR?: boolean;
  violations?: {
    showOnlyFutureDefault?: boolean;
  };
}

const initialState: IUISettingsState = {
  flightReleaseAutoSelectContent: false,
  delayCodeFormat: ["title", "delayCode.delayNumber", " - ", "delayCode.description"],
  flightTimelineCopyTextFormat: [
    "DEP ",
    "originAirport.IATACode",
    "\t\t",
    ["HHmm[L] - DDMMM", "originAirport.timezoneId", "actualOut"],
    " (",
    ["HHmm[Z] - DDMMM", "UTC", "actualOut"],
    ")\n",
    "ARR ",
    "destAirport.IATACode",
    "\t\t",
    ["HHmm[L] - DDMMM", "destAirport.timezoneId", "actualIn"],
    " (",
    ["HHmm[Z] - DDMMM", "UTC", "actualIn"],
    ")\n\n",
  ],
  flightCancellationReasons: [],
  delayCodeAllowControllable: false,
  flightTimesEqual: false,
  enableVFR: false,
  violations: {
    showOnlyFutureDefault: false,
  },
};

export const getUISettings = createAsyncThunk("uisettings/get", async () => {
  const settings: IClientSettings = await getClientSetting(ClientSettingName.UISettings);
  return settings?.setValue ?? [];
});

export const UISettings: any = createSlice({
  name: "UISettings",
  initialState,
  reducers: {
    setViolationsShowOnlyFutureDefault: (state, action: PayloadAction<boolean>) => {
      if (state.violations) {
        state.violations.showOnlyFutureDefault = action.payload;
      } else {
        state.violations = { showOnlyFutureDefault: action.payload };
      }
    },
  },
  extraReducers(builder) {
    builder.addCase(getUISettings.fulfilled, (state, action: PayloadAction<Partial<IUISettingsState>>) => {
      if (action.payload.hasOwnProperty("flightReleaseAutoSelectContent")) {
        state.flightReleaseAutoSelectContent = action.payload.flightReleaseAutoSelectContent;
      }
      if (action.payload.hasOwnProperty("delayCodeFormat")) {
        state.delayCodeFormat = action.payload.delayCodeFormat;
      }
      if (action.payload.hasOwnProperty("flightTimelineCopyTextFormat")) {
        state.flightTimelineCopyTextFormat = action.payload.flightTimelineCopyTextFormat;
      }
      if (action.payload.hasOwnProperty("flightCancellationReasons")) {
        state.flightCancellationReasons = action.payload.flightCancellationReasons;
      }
      if (action.payload.hasOwnProperty("delayCodeAllowControllable")) {
        state.delayCodeAllowControllable = action.payload.delayCodeAllowControllable;
      }
      if (action.payload.hasOwnProperty("flightTimesEqual")) {
        state.flightTimesEqual = action.payload.flightTimesEqual;
      }
      if (action.payload.hasOwnProperty("delayCodeSortOrder")) {
        state.delayCodeSortOrder = action.payload.delayCodeSortOrder;
      }
      if (action.payload.hasOwnProperty("crewScheduleFetchIntervalDays")) {
        state.crewScheduleFetchIntervalDays = action.payload.crewScheduleFetchIntervalDays;
      }
      if (action.payload.hasOwnProperty("enableVFR")) {
        state.enableVFR = action.payload.enableVFR;
      }
      if (action.payload.hasOwnProperty("violations")) {
        state.violations = action.payload.violations;
      }
    });
  },
});

export default UISettings.reducer;
